import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { formatAbsoluteDate, formatSmartDate } from '../utils/attachmentsMacroUtils.ts';

type SmartDateProps = {
	dateString: string;
};

/**
 * SSR-safe wrapper for `formatSmartDate`.
 *
 * Renders the absolute date on first render (server + client first paint),
 * then swaps to relative time ("3 hours ago") after mount — preventing
 * React hydration mismatches when SSR and client cross a time boundary.
 *
 * See: PR comment on `formatSmartDate` SSR hydration risk (PR #382918).
 *
 * TTVC note: the post-mount swap only re-renders a small text node inside an
 * already-painted table cell and never changes the cell's size, so it causes no
 * layout shift and has no measurable TTVC impact. If a future requirement needs
 * relative time visible before hydration, the alternatives are
 * `suppressHydrationWarning` on the date cell, or rendering relative time on the
 * server with a tolerated diff window.
 */
export const SmartDate = ({ dateString }: SmartDateProps): React.JSX.Element => {
	const intl = useIntl();
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	const formatted = mounted
		? formatSmartDate(dateString, intl)
		: formatAbsoluteDate(dateString, intl);

	return <>{formatted}</>;
};
